/**
 * Unified AI error handler for 429 CREDITS_EXCEEDED and 403 FEATURE_NOT_AVAILABLE.
 *
 * Backend contract (backend/app/auth.py check_ai_rate_limit):
 *   429 detail: { error: "CREDITS_EXCEEDED", message, api_type, credit_cost, credits_remaining, daily_limit, tier }
 *   403 detail: { error: "FEATURE_NOT_AVAILABLE", message, api_type, tier, upgrade_required }
 *
 * Any component hitting an AI endpoint should call:
 *
 *   if (await handleAIError(response)) return;   // stop processing — modal is shown
 *
 * A single <AILimitModal /> mounted at the app root listens for the dispatched
 * `ai-limit-error` CustomEvent and renders the unified error UX.
 */

export async function handleAIError(response) {
  if (!response || (response.status !== 429 && response.status !== 403)) {
    return false;
  }

  let detail = null;
  try {
    const body = await response.clone().json();
    detail = body?.detail || body;
  } catch (_) {
    return false;
  }

  if (!detail || (detail.error !== 'CREDITS_EXCEEDED' && detail.error !== 'FEATURE_NOT_AVAILABLE')) {
    return false;
  }

  window.dispatchEvent(new CustomEvent('ai-limit-error', { detail }));
  return true;
}

/**
 * Convenience wrapper: throw an Error if the response is an AI limit error (caller catches).
 * Use when you'd rather early-return via try/catch.
 */
export async function throwIfAIError(response) {
  if (await handleAIError(response)) {
    const err = new Error('AI_LIMIT_ERROR');
    err.aiLimitHandled = true;
    throw err;
  }
}
